import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import SearchableSelect from '../components/SearchableSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  IndianRupee, 
  Download,
  CheckCircle2,
  Clock,
  User,
  Phone,
  Eye
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_TYPES = ['Entry Fee', 'Commission', 'Bank Commission', 'Cheque Received'];

const PaymentsPage = () => {
  const { isAdmin } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loanFiles, setLoanFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');
  const _today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(_today.getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(_today.getMonth() / 3) + 1);
  const [selectedPeriodYear, setSelectedPeriodYear] = useState(_today.getFullYear());
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const periodYears = [_today.getFullYear() - 2, _today.getFullYear() - 1, _today.getFullYear(), _today.getFullYear() + 1];
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    payment_type: 'Entry Fee',
    customer_name: '',
    customer_phone: '',
    loan_file_id: '',
    loan_file_number: '',
    bank_name: '',
    amount: '',
    due_date: '',
    status: 'Pending',
    remarks: ''
  });

  // Calculate date range based on period filter
  // NOTE: We build YYYY-MM-DD strings manually to avoid the timezone bug where
  // new Date(y, m, 1).toISOString() converts to UTC, shifting IST dates by ~5.5h
  // and returning the previous day (e.g., 1 May IST -> 30 Apr UTC).
  const ymd = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const getDateRange = (period) => {
    const today = new Date();
    let start, end;
    
    switch(period) {
      case 'today':
        start = end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = ymd(weekStart.getFullYear(), weekStart.getMonth() + 1, weekStart.getDate());
        end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      }
      case 'month': {
        const m = selectedMonth;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, m, 0).getDate();
        start = ymd(y, m, 1);
        end = ymd(y, m, lastDay);
        break;
      }
      case 'quarter': {
        const qStartMonth = (selectedQuarter - 1) * 3 + 1;
        const qEndMonth = qStartMonth + 2;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, qEndMonth, 0).getDate();
        start = ymd(y, qStartMonth, 1);
        end = ymd(y, qEndMonth, lastDay);
        break;
      }
      case 'year':
        start = ymd(selectedPeriodYear, 1, 1);
        end = ymd(selectedPeriodYear, 12, 31);
        break;
      default:
        return null;
    }
    return { start, end };
  };

  // Filter payments by period
  const getFilteredPayments = () => {
    if (periodFilter === 'all') return payments;
    
    const range = getDateRange(periodFilter);
    if (!range) return payments;
    
    return payments.filter(p => {
      const paymentDate = p.paid_date || p.due_date || p.created_at?.split('T')[0];
      if (!paymentDate) return true; // Include payments without date
      return paymentDate >= range.start && paymentDate <= range.end;
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, loanFilesRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/loan-files`)
      ]);
      setPayments(paymentsRes.data);
      setLoanFiles(loanFilesRes.data);
    } catch (error) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0
      };
      
      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, payload);
        toast.success('Payment updated successfully');
      } else {
        await axios.post(`${API}/payments`, payload);
        toast.success('Payment created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      payment_type: payment.payment_type,
      customer_name: payment.customer_name,
      customer_phone: payment.customer_phone || '',
      loan_file_id: payment.loan_file_id || '',
      loan_file_number: payment.loan_file_number || '',
      bank_name: payment.bank_name || '',
      amount: payment.amount?.toString() || '',
      due_date: payment.due_date || '',
      status: payment.status || 'Pending',
      remarks: payment.remarks || ''
    });
    setIsDialogOpen(true);
  };

  const handleMarkAsPaid = async (paymentId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.put(`${API}/payments/${paymentId}`, { 
        status: 'Paid', 
        paid_date: today 
      });
      toast.success('Payment marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Error updating payment');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchData();
    } catch (error) {
      toast.error('Error deleting payment');
    }
  };

  const handleLoanFileSelect = (fileId) => {
    const file = loanFiles.find(f => f.id === fileId);
    if (file) {
      setFormData(prev => ({
        ...prev,
        loan_file_id: file.id,
        loan_file_number: file.file_number,
        customer_name: file.customer_name,
        customer_phone: file.customer_phone || '',
        bank_name: file.bank_agent_name || ''
      }));
    }
  };

  const resetForm = () => {
    setEditingPayment(null);
    setFormData({
      payment_type: 'Entry Fee',
      customer_name: '',
      customer_phone: '',
      loan_file_id: '',
      loan_file_number: '',
      bank_name: '',
      amount: '',
      due_date: '',
      status: 'Pending',
      remarks: ''
    });
  };

  const exportToExcel = () => {
    const exportData = customerData.map(c => ({
      'Customer Name': c.customer_name,
      'Phone': c.customer_phone || '-',
      'Entry Fee': c.entryFee,
      'Entry Fee Status': c.entryFeeStatus,
      'Commission': c.commission,
      'Commission Status': c.commissionStatus,
      'Bank Commission': c.bankCommission,
      'Bank Commission Status': c.bankCommissionStatus,
      'Cheque Received': c.chequeReceived,
      'Cheque Status': c.chequeStatus,
      'Total Received': c.totalReceived,
      'Total Pending': c.totalPending
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments by Customer');
    
    XLSX.writeFile(wb, `Payments_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
    toast.success('Report exported successfully');
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  // Group payments by customer
  const getCustomerData = () => {
    const customers = {};
    const filteredPayments = getFilteredPayments();
    
    filteredPayments.forEach(payment => {
      const customerKey = payment.customer_name || 'Unknown';
      if (!customers[customerKey]) {
        customers[customerKey] = {
          customer_name: payment.customer_name,
          customer_phone: payment.customer_phone,
          payments: [],
          entryFee: 0,
          entryFeeStatus: '-',
          entryFeePending: 0,
          commission: 0,
          commissionStatus: '-',
          commissionPending: 0,
          bankCommission: 0,
          bankCommissionStatus: '-',
          bankCommissionPending: 0,
          chequeReceived: 0,
          chequeStatus: '-',
          chequePending: 0,
          totalReceived: 0,
          totalPending: 0,
          lastPaymentDate: null,
          createdDate: null
        };
      }
      
      customers[customerKey].payments.push(payment);
      const amount = payment.amount || 0;
      const isPaid = payment.status === 'Paid';
      
      // Track dates
      const paymentDate = payment.paid_date || payment.due_date || payment.created_at?.split('T')[0];
      if (paymentDate) {
        if (!customers[customerKey].lastPaymentDate || paymentDate > customers[customerKey].lastPaymentDate) {
          customers[customerKey].lastPaymentDate = paymentDate;
        }
        if (!customers[customerKey].createdDate || paymentDate < customers[customerKey].createdDate) {
          customers[customerKey].createdDate = paymentDate;
        }
      }
      
      switch(payment.payment_type) {
        case 'Entry Fee':
        case 'Entry Payment':
          if (isPaid) customers[customerKey].entryFee += amount;
          customers[customerKey].entryFeeStatus = isPaid ? 'Paid' : 'Pending';
          if (!isPaid) customers[customerKey].entryFeePending += amount;
          break;
        case 'Commission':
        case 'Commission by Customer':
          if (isPaid) customers[customerKey].commission += amount;
          customers[customerKey].commissionStatus = isPaid ? 'Paid' : 'Pending';
          if (!isPaid) customers[customerKey].commissionPending += amount;
          break;
        case 'Bank Commission':
        case 'Commission by Bank':
          if (isPaid) customers[customerKey].bankCommission += amount;
          customers[customerKey].bankCommissionStatus = isPaid ? 'Paid' : 'Pending';
          if (!isPaid) customers[customerKey].bankCommissionPending += amount;
          break;
        case 'Cheque Received':
          if (isPaid) customers[customerKey].chequeReceived += amount;
          customers[customerKey].chequeStatus = isPaid ? 'Received' : 'Pending';
          if (!isPaid) customers[customerKey].chequePending += amount;
          break;
      }
      
      if (isPaid) {
        customers[customerKey].totalReceived += amount;
      } else {
        customers[customerKey].totalPending += amount;
      }
    });
    
    return Object.values(customers).filter(c => 
      c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const customerData = getCustomerData();

  // Calculate totals
  const totals = customerData.reduce((acc, c) => ({
    entryFee: acc.entryFee + c.entryFee,
    commission: acc.commission + c.commission,
    bankCommission: acc.bankCommission + c.bankCommission,
    chequeReceived: acc.chequeReceived + c.chequeReceived,
    totalReceived: acc.totalReceived + c.totalReceived,
    totalPending: acc.totalPending + c.totalPending
  }), { entryFee: 0, commission: 0, bankCommission: 0, chequeReceived: 0, totalReceived: 0, totalPending: 0 });

  const openCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsDetailDialogOpen(true);
  };

  const openAddForCustomer = (customerName, customerPhone, paymentType) => {
    setFormData({
      ...formData,
      customer_name: customerName,
      customer_phone: customerPhone || '',
      payment_type: paymentType
    });
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="payments-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Payments by Customer</h1>
          <p className="text-slate-500 mt-1">Track Entry Fee, Commission & Bank Commission per Customer</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="border-brand-navy text-brand-navy"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="bg-brand-navy text-white hover:bg-brand-navy/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment
          </Button>
        </div>
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'all', label: 'All Time' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`payments-period-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}
        {periodFilter === 'month' && (
          <>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{monthNames.map((n, i) => <SelectItem key={i+1} value={(i+1).toString()}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'quarter' && (
          <>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[170px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'year' && (
          <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{formatCurrency(totals.entryFee)}</p>
            <p className="text-xs text-purple-500">Entry Fee</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(totals.commission)}</p>
            <p className="text-xs text-blue-500">Commission</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totals.bankCommission)}</p>
            <p className="text-xs text-green-500">Bank Commission</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{formatCurrency(totals.chequeReceived)}</p>
            <p className="text-xs text-amber-500">Cheque Received</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-emerald-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totals.totalReceived)}</p>
            <p className="text-xs text-emerald-500">Total Received</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-red-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.totalPending)}</p>
            <p className="text-xs text-red-500">Total Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Badge className="bg-slate-100 text-slate-600">
          {customerData.length} Customers
        </Badge>
      </div>

      {/* Customer Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-500 text-center">Date</TableHead>
                <TableHead className="font-semibold text-purple-600 text-center">Entry Fee</TableHead>
                <TableHead className="font-semibold text-blue-600 text-center">Commission</TableHead>
                <TableHead className="font-semibold text-green-600 text-center">Bank Commission</TableHead>
                <TableHead className="font-semibold text-amber-600 text-center">Cheque Received</TableHead>
                <TableHead className="font-semibold text-emerald-600 text-center">Total Received</TableHead>
                <TableHead className="font-semibold text-red-600 text-center">Pending</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customerData.map((customer) => (
                <TableRow key={customer.customer_name} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-brand-navy/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-brand-navy" />
                      </div>
                      <div>
                        <p className="font-semibold text-brand-navy">{customer.customer_name}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.customer_phone || 'No phone'}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div>
                      {customer.lastPaymentDate ? (
                        <p className="text-sm text-slate-600">
                          {new Date(customer.lastPaymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400">No date</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div>
                      <p className={`font-semibold ${customer.entryFee > 0 ? 'text-purple-700' : 'text-slate-300'}`}>
                        {customer.entryFee > 0 ? formatCurrency(customer.entryFee) : '-'}
                      </p>
                      {customer.entryFee > 0 && (
                        <Badge className={`text-xs ${customer.entryFeeStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {customer.entryFeeStatus}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div>
                      <p className={`font-semibold ${customer.commission > 0 ? 'text-blue-700' : 'text-slate-300'}`}>
                        {customer.commission > 0 ? formatCurrency(customer.commission) : '-'}
                      </p>
                      {customer.commission > 0 && (
                        <Badge className={`text-xs ${customer.commissionStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {customer.commissionStatus}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div>
                      <p className={`font-semibold ${customer.bankCommission > 0 ? 'text-green-700' : 'text-slate-300'}`}>
                        {customer.bankCommission > 0 ? formatCurrency(customer.bankCommission) : '-'}
                      </p>
                      {customer.bankCommission > 0 && (
                        <Badge className={`text-xs ${customer.bankCommissionStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {customer.bankCommissionStatus}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div>
                      <p className={`font-semibold ${customer.chequeReceived > 0 ? 'text-amber-700' : 'text-slate-300'}`}>
                        {customer.chequeReceived > 0 ? formatCurrency(customer.chequeReceived) : '-'}
                      </p>
                      {customer.chequeReceived > 0 && (
                        <Badge className={`text-xs ${customer.chequeStatus === 'Received' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {customer.chequeStatus}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <p className="font-bold text-emerald-700">{formatCurrency(customer.totalReceived)}</p>
                  </TableCell>
                  <TableCell className="text-center">
                    <p className={`font-bold ${customer.totalPending > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                      {customer.totalPending > 0 ? formatCurrency(customer.totalPending) : '-'}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openCustomerDetails(customer)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openAddForCustomer(customer.customer_name, customer.customer_phone, 'Entry Fee')}
                        className="text-purple-600"
                        title="Add Entry Fee"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {customerData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                    <IndianRupee className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-lg font-medium">No payment records found</p>
                    <p className="text-sm">Click "Add Payment" to create your first record</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Customer Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy flex items-center gap-2">
              <User className="h-5 w-5" />
              {selectedCustomer?.customer_name} - Payment Details
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-0 bg-purple-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-purple-700">{formatCurrency(selectedCustomer.entryFee)}</p>
                    <p className="text-xs text-purple-500">Entry Fee</p>
                  </CardContent>
                </Card>
                <Card className="border-0 bg-blue-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-blue-700">{formatCurrency(selectedCustomer.commission)}</p>
                    <p className="text-xs text-blue-500">Commission</p>
                  </CardContent>
                </Card>
                <Card className="border-0 bg-green-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-green-700">{formatCurrency(selectedCustomer.bankCommission)}</p>
                    <p className="text-xs text-green-500">Bank Commission</p>
                  </CardContent>
                </Card>
                <Card className="border-0 bg-amber-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-amber-700">{formatCurrency(selectedCustomer.chequeReceived)}</p>
                    <p className="text-xs text-amber-500">Cheque</p>
                  </CardContent>
                </Card>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedCustomer.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700">{payment.payment_type}</Badge>
                      </TableCell>
                      <TableCell className="font-semibold">{formatCurrency(payment.amount)}</TableCell>
                      <TableCell>
                        <Badge className={payment.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {payment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {payment.paid_date || payment.due_date || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {payment.status === 'Pending' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkAsPaid(payment.id)}
                              className="text-green-600"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { handleEdit(payment); setIsDetailDialogOpen(false); }}
                            className="text-blue-500"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(payment.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="flex gap-2">
                {PAYMENT_TYPES.map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant="outline"
                    onClick={() => { 
                      openAddForCustomer(selectedCustomer.customer_name, selectedCustomer.customer_phone, type);
                      setIsDetailDialogOpen(false);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {type}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payment Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              {editingPayment ? 'Edit Payment' : 'Add Payment'}
            </DialogTitle>
            <DialogDescription>
              {editingPayment ? 'Update payment details' : 'Create a new payment record'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payment Type *</Label>
              <Select value={formData.payment_type} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_type: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Loan File (Optional)</Label>
              <SearchableSelect
                options={loanFiles.map(f => ({ value: f.id, label: f.file_number, sublabel: f.customer_name }))}
                value=""
                onChange={handleLoanFileSelect}
                placeholder="Choose loan file to auto-fill"
                searchPlaceholder="Search by file no or customer name..."
                emptyText="No loan files found."
                dataTestId="select-loan-file"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.customer_name || !formData.amount}
            >
              {editingPayment ? 'Update' : 'Add'} Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
